<script setup lang="ts">
import { ApiError } from '@/api/client'
import {
  deleteDiveCenter,
  getDiveCenter,
  getGooglePlaceDetails,
  publishDiveCenter,
  unpublishDiveCenter,
  updateDiveCenter,
} from '@/api/dive-centers'
import { listOfferings } from '@/api/offerings'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { usePermissions } from '@/composables/usePermissions'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import { hasAnyOpenDay } from '@/data/openingHours'
import { useDiveCentersStore } from '@/stores/diveCenters'
import type { DiveCenter, GooglePlaceDetails, Offering } from '@/types'
import type { RouteLocationRaw } from 'vue-router'

definePage({ meta: { requiresAuth: true } })

/*
 * Configurar el centro es de owner, admin y manager. `staff` ve el Overview
 * entero —el estado, lo que falta, las métricas— pero no las acciones que lo
 * cambian: sin esto, los botones estaban ahí y el 403 llegaba después de
 * apretarlos, que es la peor forma de enterarse de un permiso.
 */
const { canConfigureCenter, isOwner } = usePermissions()

const route = useRoute('dive-centers-id')
const router = useRouter()
const diveCenters = useDiveCentersStore()
const deleting = ref(false)

const id = String(route.params.id)
const diveCenter = ref<DiveCenter | null>(null)
const loading = ref(true)
const loadError = ref('')

// Publicación. Vivía en Profile, pero el estado del centro es asunto del
// Overview: acá está al lado de lo que falta para poder publicar.
const publishing = ref(false)
const missingDialog = ref(false)
const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

const gDetails = ref<GooglePlaceDetails | null>(null)
const offerings = ref<Offering[] | null>(null)

const load = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const res = await getDiveCenter(id)

    diveCenter.value = res.diveCenter
  }
  catch (e) {
    loadError.value = e instanceof ApiError ? e.message : 'Could not load dive center.'
  }
  finally {
    loading.value = false
  }

  // Best-effort: alimenta la métrica y los avisos, pero no bloquea el render.
  try {
    const res = await listOfferings(id)

    offerings.value = res.offerings
  }
  catch {
    offerings.value = null
  }
}

onMounted(load)

watch(
  () => diveCenter.value?.googlePlaceId,
  async placeId => {
    gDetails.value = null
    if (!placeId)
      return

    try {
      gDetails.value = await getGooglePlaceDetails(id)
    }
    catch {
      gDetails.value = null
    }
  },
)

const dc = computed(() => diveCenter.value)
const photoCount = computed(() => dc.value?.photos?.length ?? 0)
const serviceCount = computed(() => dc.value?.services?.length ?? 0)
const agencyCount = computed(() => dc.value?.agencies?.length ?? 0)

/*
 * Cada sección del perfil es su propia ruta desde §19 (antes eran tabs con
 * `?tab=`), así que los avisos enlazan directo a la que corresponde.
 */
/*
 * El perfil entero se edita con un wizard; `?step=` abre el paso que resuelve
 * cada aviso (sin fotos → paso de fotos, Google sin vincular → paso de Google).
 */
const profileTo = (step?: string): RouteLocationRaw =>
  ({ name: 'dive-centers-id-profile-edit', params: { id }, ...(step ? { query: { step } } : {}) })

/**
 * Lo que le falta a este centro para estar completo.
 *
 * Es la razón de ser de esta pantalla: antes era una grilla de accesos que
 * repetía el menú lateral. Ahora responde "¿qué me falta?" en vez de "¿a dónde
 * puedo ir?", que es lo que el sidebar ya contesta.
 *
 * Solo se listan cosas accionables por el usuario. La verificación de la cuenta
 * queda afuera: la resuelve Blue Trails, no el proveedor, y aparece como badge.
 */
interface Todo {
  key: string
  icon: string
  color: string
  title: string
  detail: string
  actionLabel: string
  to: RouteLocationRaw
  /**
   * Se puede sacar de la lista para siempre.
   *
   * Solo los avisos que NO se apagan solos: un centro sin ficha de Google
   * Business y sin intención de tenerla va a leer ese mismo aviso todos los días
   * del resto de su vida, y un bloque que siempre tiene algo deja de leerse.
   * Los que se resuelven haciendo el trabajo —publicar, cargar ofertas— no son
   * descartables: taparlos sería esconder lo que falta.
   */
  dismissible?: boolean
}

const todos = computed<Todo[]>(() => {
  const c = dc.value
  if (!c)
    return []

  const items: Todo[] = []

  if (!photoCount.value) {
    items.push({
      key: 'photos',
      icon: 'ri-image-line',
      color: 'warning',
      title: 'No photos yet',
      detail: 'Centres with photos get noticeably more attention in the app.',
      actionLabel: 'Add photos',
      to: profileTo('photos'),
      dismissible: true,
    })
  }

  if (!c.googlePlaceId) {
    items.push({
      key: 'google',
      icon: 'ri-google-line',
      color: 'info',
      title: 'Google Business not linked',
      detail: 'Link it to show your rating and reviews on your profile.',
      actionLabel: 'Link Google',
      to: profileTo('google'),
      dismissible: true,
    })
  }

  if (offerings.value != null && !offerings.value.length) {
    items.push({
      key: 'no-offerings',
      icon: 'ri-price-tag-3-line',
      color: 'warning',
      title: 'No offerings yet',
      detail: 'Add the courses, dives and rentals this centre sells.',
      actionLabel: 'Add offerings',
      to: { name: 'dive-centers-id-offerings', params: { id } },
    })
  }

  // Una oferta sin precio se publica igual, pero el buceador no puede
  // compararla — en la práctica es una oferta a medias.
  const priceless = (offerings.value ?? []).filter(o => o.price?.amount == null)

  if (priceless.length) {
    items.push({
      key: 'priceless',
      icon: 'ri-money-euro-circle-line',
      color: 'info',
      title: `${priceless.length} offering${priceless.length === 1 ? '' : 's'} without a price`,
      detail: 'Divers filter and compare by price, so these are easy to miss.',
      actionLabel: 'Review offerings',
      to: { name: 'dive-centers-id-offerings', params: { id } },
      dismissible: true,
    })
  }

  // El draft va último: es la consecuencia de lo anterior, no la causa.
  if (c.status === 'draft') {
    items.push({
      key: 'draft',
      icon: 'ri-eye-off-line',
      color: 'warning',
      title: 'Not published yet',
      detail: 'While it stays a draft, divers can\'t find this centre in the app.',
      actionLabel: 'Complete profile',
      to: profileTo(),
    })
  }

  return items.filter(t => !(c.dismissedTodos ?? []).includes(t.key))
})

/*
 * Descartar un aviso.
 *
 * Se guarda en el centro con un PATCH normal, no en el navegador: es una
 * decisión del negocio ("no tenemos Google Business"), no una preferencia de
 * quien está mirando, y en localStorage volvería a aparecer en la próxima
 * computadora y le seguiría apareciendo al resto del equipo.
 *
 * La lista se actualiza en el acto y se revierte si el guardado falla: esperar
 * la respuesta para que el aviso desaparezca hace que el botón se sienta roto.
 */
const dismissing = ref('')

const dismiss = async (key: string) => {
  const c = dc.value
  if (!c || dismissing.value)
    return

  const previous = c.dismissedTodos ?? []
  if (previous.includes(key))
    return

  const next = [...previous, key]

  dismissing.value = key
  diveCenter.value = { ...c, dismissedTodos: next }
  try {
    await updateDiveCenter(id, { dismissedTodos: next })
  }
  catch (e) {
    diveCenter.value = { ...c, dismissedTodos: previous }
    notifyError(apiMessage(e, 'Could not dismiss this reminder.'))
  }
  finally {
    dismissing.value = ''
  }
}

/*
 * Completitud del perfil: proporción de campos recomendados completados. Empuja
 * a un listado publicable y atractivo, más allá del gate mínimo de publicación.
 */
const completeness = computed(() => {
  const d = diveCenter.value

  if (!d)
    return 0

  const a = d.address
  const addressOk = !!(a?.formattedAddress?.trim() || (a?.street?.trim() && a?.city?.trim() && a?.country?.trim()))

  const checks = [
    !!d.description?.trim(),
    !!d.contactEmail?.trim(),
    !!d.contactPhone?.trim(),
    addressOk,
    !!d.coordinates,
    hasAnyOpenDay(d.openingHours),
    photoCount.value > 0,
    agencyCount.value > 0,
    serviceCount.value > 0,
    !!d.googlePlaceId,
  ]

  return Math.round((checks.filter(Boolean).length / checks.length) * 100)
})

// Gate de publicación (refleja lo que valida el backend).
const checklist = computed(() => {
  const d = diveCenter.value
  const a = d?.address
  const addressOk = !!(a?.formattedAddress?.trim() || (a?.street?.trim() && a?.city?.trim() && a?.country?.trim()))

  return [
    { key: 'email', label: 'Contact email', ok: !!d?.contactEmail?.trim() },
    { key: 'phone', label: 'Contact phone', ok: !!d?.contactPhone?.trim() },
    { key: 'address', label: 'Address', ok: addressOk },
  ]
})

const canPublish = computed(() => checklist.value.every(c => c.ok))
const isPublished = computed(() => diveCenter.value?.status === 'published')

/*
 * Borrar el centro vive acá y no en Basic info: es una acción sobre el centro
 * entero, y desde una sección del perfil se leía como "borrar esta sección".
 */
const removeCenter = async () => {
  if (!diveCenter.value)
    return

  const ok = await confirmRef.value?.open({
    title: 'Delete dive center',
    message: `Delete "${diveCenter.value.name}"? This also removes its photos. This cannot be undone.`,
    confirmText: 'Delete',
    confirmColor: 'error',
  })

  if (!ok)
    return

  deleting.value = true
  try {
    const name = diveCenter.value.name

    await deleteDiveCenter(id)
    await diveCenters.load(true)
    notifySuccess(`"${name}" deleted.`)
    router.replace({ name: 'dive-centers' })
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not delete dive center.'))
    deleting.value = false
  }
}

/*
 * Indicadores de la cabecera. El orden lo pidió el usuario y va de lo que más
 * se toca a lo que menos: las offerings cambian todo el tiempo, la completitud
 * es la consecuencia de lo demás.
 */
const stats = computed(() => [
  { label: 'Offerings', value: offerings.value?.length ?? '—', icon: 'ri-shopping-bag-3-line', color: 'primary' },
  { label: 'Services', value: serviceCount.value, icon: 'ri-price-tag-3-line', color: 'info' },
  { label: 'Certifications', value: agencyCount.value, icon: 'ri-award-line', color: 'success' },
  { label: 'Photos', value: photoCount.value, icon: 'ri-image-line', color: 'warning' },
  { label: 'Completeness', value: `${completeness.value}%`, icon: 'ri-donut-chart-line', color: 'secondary' },
])

const applyPublish = async (publish: boolean) => {
  publishing.value = true
  try {
    const res = publish ? await publishDiveCenter(id) : await unpublishDiveCenter(id)

    diveCenter.value = res.diveCenter
    notifySuccess(publish ? 'Dive center published.' : 'Dive center unpublished.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not change publish state.'))
  }
  finally {
    publishing.value = false
  }
}

const onPublishClick = async () => {
  if (!diveCenter.value)
    return

  if (isPublished.value) {
    const ok = await confirmRef.value?.open({
      title: 'Unpublish dive center',
      message: 'It will be hidden from the app until you publish it again. Continue?',
      confirmText: 'Unpublish',
      confirmColor: 'error',
    })

    if (ok)
      await applyPublish(false)

    return
  }

  // Al intentar publicar incompleto: bloquear y mostrar qué falta.
  if (!canPublish.value) {
    missingDialog.value = true

    return
  }

  const ok = await confirmRef.value?.open({
    title: 'Publish dive center',
    message: 'Once your account is verified, it will be visible to divers in the app. Continue?',
    confirmText: 'Publish',
    confirmColor: 'primary',
  })

  if (ok)
    await applyPublish(true)
}

/**
 * Secciones todavía no construidas. No están en el menú lateral a propósito
 * (un menú de items muertos desorienta), pero se anuncian acá para que se vea
 * hacia dónde va el producto.
 */
const comingSoon = [
  { title: 'Bookings', icon: 'ri-calendar-check-line' },
  { title: 'Reviews', icon: 'ri-star-line' },
  { title: 'Messages', icon: 'ri-message-2-line' },
  { title: 'Analytics', icon: 'ri-line-chart-line' },
  { title: 'Reports', icon: 'ri-file-chart-line' },
  { title: 'Share', icon: 'ri-share-line' },
]
</script>

<template>
  <div class="mx-auto" style="max-width: 1100px;">
    <!-- Sin enlace de vuelta: "All dive centers" ya está en el menú lateral
         cuando la cuenta tiene más de un centro. -->
    <div
      v-if="loading"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <VAlert
      v-else-if="loadError"
      type="error"
      variant="tonal"
      density="compact"
    >
      {{ loadError }}
    </VAlert>

    <template v-else-if="diveCenter">
      <DiveCenterHeader
        :dive-center="diveCenter"
        :g-details="gDetails"
      >
        <template #actions>
          <VBtn
            v-if="canConfigureCenter"
            :to="profileTo()"
            variant="outlined"
            prepend-icon="ri-store-2-line"
          >
            Edit profile
          </VBtn>

          <VBtn
            v-if="canConfigureCenter"
            :color="isPublished ? 'secondary' : 'primary'"
            :variant="isPublished ? 'outlined' : 'flat'"
            :prepend-icon="isPublished ? 'ri-eye-off-line' : 'ri-upload-cloud-line'"
            :loading="publishing"
            @click="onPublishClick"
          >
            {{ isPublished ? 'Unpublish' : 'Publish' }}
          </VBtn>

          <!-- El menú es solo el borrado del centro, que ya era de owner y
               admin en el backend: sin permiso no queda nada adentro. -->
          <VMenu
            v-if="isOwner"
            location="bottom end"
          >
            <template #activator="{ props }">
              <VBtn
                v-bind="props"
                icon="ri-more-2-line"
                variant="text"
                color="default"
                :loading="deleting"
              />
            </template>
            <VList density="comfortable">
              <VListItem
                prepend-icon="ri-delete-bin-line"
                title="Delete dive center"
                base-color="error"
                @click="removeCenter"
              />
            </VList>
          </VMenu>
        </template>
      </DiveCenterHeader>

      <!-- Indicadores y pendientes comparten fila: en horizontal los cinco
           números ocupaban una franja entera para media línea de contenido. -->
      <VRow class="mb-2">
        <VCol
          cols="12"
          md="4"
        >
          <VCard class="h-100">
            <VCardText>
              <div class="d-flex flex-column ga-5">
                <div
                  v-for="m in stats"
                  :key="m.label"
                  class="d-flex align-center ga-3"
                >
                  <VAvatar
                    :color="m.color"
                    rounded="lg"
                    size="42"
                  >
                    <VIcon
                      :icon="m.icon"
                      size="22"
                      color="white"
                    />
                  </VAvatar>
                  <div>
                    <div class="text-body-2 text-medium-emphasis">
                      {{ m.label }}
                    </div>
                    <div class="text-h6 font-weight-medium">
                      {{ m.value }}
                    </div>
                  </div>
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="8"
        >
          <!-- Necesita atención -->
          <VCard class="pa-6 h-100">
            <h2 class="text-h6 font-weight-bold mb-1">
              Needs attention
            </h2>

            <template v-if="todos.length">
              <p class="text-body-2 text-medium-emphasis mb-5">
                Finish these to get the most out of your listing.
              </p>

              <VList class="py-0">
                <VListItem
                  v-for="t in todos"
                  :key="t.key"
                  class="px-0"
                >
                  <template #prepend>
                    <VAvatar
                      :color="t.color"
                      variant="tonal"
                      rounded="lg"
                      size="40"
                      class="me-3"
                    >
                      <VIcon :icon="t.icon" />
                    </VAvatar>
                  </template>

                  <VListItemTitle class="font-weight-medium">
                    {{ t.title }}
                  </VListItemTitle>
                  <VListItemSubtitle class="text-wrap">
                    {{ t.detail }}
                  </VListItemSubtitle>

                  <!--
                    Staff ve QUÉ falta pero no las acciones: los destinos son
                    pantallas de configuración de las que el guard lo rebota.
                    Se deja el aviso porque saber que el centro está sin
                    publicar le sirve igual, aunque no sea quien lo publica.
                  -->
                  <template
                    v-if="canConfigureCenter"
                    #append
                  >
                    <VBtn
                      :to="t.to"
                      variant="text"
                      size="small"
                      append-icon="ri-arrow-right-line"
                    >
                      {{ t.actionLabel }}
                    </VBtn>

                    <!--
                      La cruz solo en los informativos. Al lado de la acción y no
                      dentro de un menú: descartar es lo que se hace con estos
                      cuando la respuesta es "no aplica", y esconderlo tras tres
                      puntitos deja el aviso ahí para siempre igual.
                    -->
                    <VBtn
                      v-if="t.dismissible"
                      icon="ri-close-line"
                      variant="text"
                      size="small"
                      color="medium-emphasis"
                      title="Dismiss — don't show this again"
                      :loading="dismissing === t.key"
                      @click="dismiss(t.key)"
                    />
                  </template>
                </VListItem>
              </VList>
            </template>

            <div
              v-else
              class="d-flex align-center ga-3 mt-2"
            >
              <VAvatar
                color="success"
                variant="tonal"
                rounded="lg"
                size="40"
              >
                <VIcon icon="ri-check-line" />
              </VAvatar>
              <div>
                <div class="text-body-1 font-weight-medium">
                  All set
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  Nothing pending on this dive center right now.
                </div>
              </div>
            </div>
          </VCard>
        </VCol>
      </VRow>

      <!-- La ficha del centro, un widget por grupo de datos. -->
      <div class="mt-6">
        <DiveCenterInfoCards
          :dive-center="diveCenter"
          :g-details="gDetails"
        />
      </div>

      <!-- "What's missing" — al intentar publicar un DC incompleto. -->
      <VDialog
        v-model="missingDialog"
        max-width="460"
      >
        <VCard>
          <VCardTitle>Complete before publishing</VCardTitle>
          <VCardText>
            <p class="text-body-2 text-medium-emphasis mb-3">
              Add the following to publish this dive center:
            </p>
            <VList
              density="compact"
              class="py-0 bg-transparent"
            >
              <VListItem
                v-for="c in checklist"
                :key="c.key"
                class="px-0"
                :title="c.label"
              >
                <template #prepend>
                  <VIcon
                    :icon="c.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'"
                    :color="c.ok ? 'success' : 'warning'"
                    size="20"
                  />
                </template>
              </VListItem>
            </VList>
          </VCardText>
          <VCardActions class="px-4 py-3">
            <VBtn
              variant="text"
              color="secondary"
              @click="missingDialog = false"
            >
              Close
            </VBtn>
            <VSpacer />
            <VBtn
              v-if="canConfigureCenter"
              prepend-icon="ri-pencil-line"
              :to="profileTo()"
            >
              Complete profile
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <ConfirmDialog ref="confirmRef" />

      <!-- Lo que viene. Discreto a propósito: informa el rumbo sin competir
           con lo que sí se puede hacer hoy. -->
      <div class="mt-6 mb-2">
        <div class="text-overline text-medium-emphasis mb-2">
          Coming soon
        </div>
        <div class="d-flex flex-wrap gap-2">
          <VChip
            v-for="s in comingSoon"
            :key="s.title"
            :prepend-icon="s.icon"
          >
            {{ s.title }}
          </VChip>
        </div>
      </div>
    </template>
  </div>
</template>
